// src/pages/RMRDC/UserManagement.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Avatar,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Select,
  FormControl,
  MenuItem,
  Snackbar,
  Alert,
  Slide,
  useMediaQuery,
  useTheme,
  alpha,
  Divider,
  Tooltip,
  LinearProgress,
  Fade,
  Zoom,
  Badge,
  Stack,
  Switch,
  FormControlLabel,
  Checkbox,
} from '@mui/material';

// ============================================
// ICON IMPORTS
// ============================================
import {
  People as PeopleIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Verified as VerifiedIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Block as BlockIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Add as AddIcon,
  Clear as ClearIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  ShoppingBag as ShoppingBagIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  CalendarToday as CalendarTodayIcon,
  Business as BusinessIcon,
  PersonAdd as PersonAddIcon,
  Person as PersonIcon,
} from '@mui/icons-material';

// Import shared components
import Preloader from '../../theme/Preloader';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import Footer from './components/Footer';
import { formatDate } from '../buyers/components/utils';

// ============================================
// ENHANCED COLOR SCHEME
// ============================================
const COLORS = {
  primary: '#1A237E',
  primaryLight: '#283593',
  primaryDark: '#0D1445',
  secondary: '#00BCD4',
  accent: '#FF6F00',
  success: '#2E7D32',
  warning: '#E65100',
  error: '#C62828',
  info: '#01579B',
  gold: '#D4AF37',
  purple: '#6A1B9A',
  teal: '#00838F',
  grey: {
    50: '#FAFAFA',
    100: '#F5F5F5',
    200: '#EEEEEE',
    300: '#E0E0E0',
    400: '#BDBDBD',
    500: '#9E9E9E',
    600: '#757575',
    700: '#616161',
    800: '#424242',
    900: '#212121',
  },
};

// ============================================
// API CONFIGURATION
// ============================================
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

const API_ENDPOINTS = {
  USERS: {
    LIST: `${API_BASE_URL}/rmrdc/users.php`,
    CREATE: `${API_BASE_URL}/rmrdc/users.php`,
    UPDATE: (id) => `${API_BASE_URL}/rmrdc/users.php?id=${id}`,
    DELETE: (id) => `${API_BASE_URL}/rmrdc/users.php?id=${id}`,
    TOGGLE_STATUS: `${API_BASE_URL}/rmrdc/users.php`,
  },
};

// ============================================
// LOGGER
// ============================================
const logger = {
  info: (message, data) => console.log(`[USER-MGMT] ✅ ${message}`, data || ''),
  error: (message, error) => console.error(`[USER-MGMT] ❌ ${message}`, error || ''),
  debug: (message, data) => console.debug(`[USER-MGMT] 🔍 ${message}`, data || ''),
  warn: (message, data) => console.warn(`[USER-MGMT] ⚠️ ${message}`, data || ''),
};

// ============================================
// CUSTOM COMPONENTS
// ============================================
const UserStatusChip = ({ status }) => {
  const config = {
    active: { label: 'Active', color: COLORS.success, icon: <CheckCircleIcon sx={{ fontSize: 14 }} /> },
    inactive: { label: 'Inactive', color: COLORS.error, icon: <BlockIcon sx={{ fontSize: 14 }} /> },
  };

  const { label, color, icon } = config[status] || config.inactive;

  return (
    <Chip
      icon={icon}
      label={label}
      size="small"
      sx={{
        bgcolor: alpha(color, 0.1),
        color: color,
        fontWeight: 700,
        border: `1px solid ${alpha(color, 0.15)}`,
        '& .MuiChip-icon': { color: color },
      }}
    />
  );
};

const UserTypeChip = ({ userType }) => {
  const config = {
    supplier: { label: 'Supplier', color: COLORS.info },
    buyer: { label: 'Buyer', color: COLORS.success },
    super_admin: { label: 'Super Admin', color: COLORS.gold },
    rmrdc: { label: 'RMRDC Admin', color: COLORS.gold },
  };

  const { label, color } = config[userType] || config.buyer;

  return (
    <Chip
      label={label}
      size="small"
      sx={{
        bgcolor: alpha(color, 0.1),
        color: color,
        fontWeight: 600,
        border: `1px solid ${alpha(color, 0.15)}`,
      }}
    />
  );
};

// ============================================
// MAIN COMPONENT
// ============================================
const UserManagement = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // ============================================
  // LOCAL STORAGE DATA
  // ============================================
  const userData = useMemo(() => {
    try {
      const data = localStorage.getItem('user_data');
      return data ? JSON.parse(data) : null;
    } catch (e) {
      return null;
    }
  }, []);

  const userType = localStorage.getItem('user_type') || 'super_admin';

  // ============================================
  // STATE
  // ============================================
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  const [selectedTab, setSelectedTab] = useState('users');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [lastRefreshed, setLastRefreshed] = useState(new Date());
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [bulkAction, setBulkAction] = useState('');

  // Dialog states
  const [userDetailsDialog, setUserDetailsDialog] = useState(false);
  const [createUserDialog, setCreateUserDialog] = useState(false);
  const [editUserDialog, setEditUserDialog] = useState(false);
  const [deleteUserDialog, setDeleteUserDialog] = useState(false);
  const [bulkActionDialog, setBulkActionDialog] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    phone: '',
    user_type: 'buyer',
    is_active: true,
    is_verified: true,
    name: '',
    organisation: '',
    industry: '',
    company_name: '',
    rc_number: '',
    office_address: '',
  });

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    verified: 0,
    unverified: 0,
    buyers: 0,
    suppliers: 0,
    admins: 0,
  });

  // ============================================
  // API FUNCTIONS
  // ============================================
  const fetchUsers = useCallback(async () => {
    logger.info('Fetching users...');
    setLoading(true);

    try {
      const userDataStr = localStorage.getItem('user_data');
      if (!userDataStr) {
        throw new Error('User data not found. Please login again.');
      }

      const userData = JSON.parse(userDataStr);
      const userId = userData.id;

      if (!userId) {
        throw new Error('User ID not found. Please login again.');
      }

      const url = `${API_ENDPOINTS.USERS.LIST}?user_id=${userId}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();

      if (result.success && result.data) {
        let usersData = [];
        
        if (Array.isArray(result.data)) {
          usersData = result.data;
        } else if (result.data.users) {
          usersData = result.data.users;
        } else if (result.data.data) {
          usersData = result.data.data;
        }
        
        setUsers(usersData);
        calculateStats(usersData);
        logger.info(`✅ Loaded ${usersData.length} users`);
      } else {
        throw new Error(result.message || 'Failed to fetch users');
      }
    } catch (error) {
      logger.error('Error fetching users:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to load users. Please try again.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  const calculateStats = (usersData) => {
    const statsData = {
      total: usersData.length,
      active: usersData.filter(u => u.is_active).length,
      inactive: usersData.filter(u => !u.is_active).length,
      verified: usersData.filter(u => u.is_verified).length,
      unverified: usersData.filter(u => !u.is_verified).length,
      buyers: usersData.filter(u => u.user_type === 'buyer').length,
      suppliers: usersData.filter(u => u.user_type === 'supplier').length,
      admins: usersData.filter(u => u.user_type === 'super_admin' || u.user_type === 'rmrdc').length,
    };
    setStats(statsData);
  };


const toggleUserStatus = useCallback(async (userId, action) => {
    try {
        const userDataStr = localStorage.getItem('user_data');
        if (!userDataStr) {
            setSnackbar({ 
                open: true, 
                message: 'User data not found. Please login again.', 
                severity: 'error' 
            });
            return;
        }

        const userData = JSON.parse(userDataStr);
        const adminId = userData.id;

        if (!adminId) {
            setSnackbar({ 
                open: true, 
                message: 'Admin ID not found. Please login again.', 
                severity: 'error' 
            });
            return;
        }

        // Use dedicated toggle endpoint
        const url = `${API_BASE_URL}/rmrdc/users.php?action=toggle_status&user_id=${adminId}`;
        
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                user_id: userId,
            }),
        });

        const result = await response.json();
        
        if (result.success) {
            setSnackbar({ 
                open: true, 
                message: result.message || 'User status updated successfully', 
                severity: 'success' 
            });
            fetchUsers();
        } else {
            throw new Error(result.message || 'Failed to update user status');
        }
    } catch (error) {
        logger.error('Error toggling user status:', error);
        setSnackbar({ 
            open: true, 
            message: error.message || 'Failed to update user status', 
            severity: 'error' 
        });
    }
}, [fetchUsers]);


const deleteUser = useCallback(async (userId) => {
    if (!userId) {
        setSnackbar({ 
            open: true, 
            message: 'Invalid user ID', 
            severity: 'error' 
        });
        return;
    }

    try {
        const userDataStr = localStorage.getItem('user_data');
        if (!userDataStr) {
            throw new Error('User data not found. Please login again.');
        }

        const userData = JSON.parse(userDataStr);
        const adminId = userData.id;

        if (!adminId) {
            throw new Error('Admin ID not found. Please login again.');
        }

        // Show loading state
        setSnackbar({ 
            open: true, 
            message: 'Deleting user...', 
            severity: 'info' 
        });

        // The URL should have id as a query parameter, not as part of the path
        const url = `${API_ENDPOINTS.USERS.DELETE(userId)}&user_id=${adminId}`;
        
        const response = await fetch(url, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
            },
        });

        const result = await response.json();
        
        if (result.success) {
            setSnackbar({ 
                open: true, 
                message: result.message || 'User deleted successfully', 
                severity: 'success' 
            });
            setDeleteUserDialog(false);
            setSelectedUser(null);
            // Refresh the user list
            fetchUsers();
        } else {
            throw new Error(result.message || 'Failed to delete user');
        }
    } catch (error) {
        logger.error('Error deleting user:', error);
        setSnackbar({ 
            open: true, 
            message: error.message || 'Failed to delete user', 
            severity: 'error' 
        });
    }
}, [fetchUsers]);

  const createUser = useCallback(async () => {
    try {
      const userDataStr = localStorage.getItem('user_data');
      if (!userDataStr) return;

      const userData = JSON.parse(userDataStr);
      const adminId = userData.id;

      const response = await fetch(`${API_ENDPOINTS.USERS.CREATE}?user_id=${adminId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();
      
      if (result.success) {
        setSnackbar({ open: true, message: 'User created successfully', severity: 'success' });
        setCreateUserDialog(false);
        resetForm();
        fetchUsers();
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
      logger.error('Error creating user:', error);
      setSnackbar({ open: true, message: error.message || 'Failed to create user', severity: 'error' });
    }
  }, [formData, fetchUsers]);

  const updateUser = useCallback(async () => {
    try {
      const userDataStr = localStorage.getItem('user_data');
      if (!userDataStr) return;

      const userData = JSON.parse(userDataStr);
      const adminId = userData.id;

      const response = await fetch(`${API_ENDPOINTS.USERS.UPDATE(selectedUser?.id)}?user_id=${adminId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();
      
      if (result.success) {
        setSnackbar({ open: true, message: 'User updated successfully', severity: 'success' });
        setEditUserDialog(false);
        setUserDetailsDialog(false);
        fetchUsers();
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
      logger.error('Error updating user:', error);
      setSnackbar({ open: true, message: error.message || 'Failed to update user', severity: 'error' });
    }
  }, [formData, selectedUser, fetchUsers]);

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      phone: '',
      user_type: 'buyer',
      is_active: true,
      is_verified: true,
      name: '',
      organisation: '',
      industry: '',
      company_name: '',
      rc_number: '',
      office_address: '',
    });
  };

  const loadData = useCallback(async () => {
    await fetchUsers();
  }, [fetchUsers]);

  // ============================================
  // EFFECTS
  // ============================================
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token || (userType !== 'super_admin' && userType !== 'rmrdc')) {
      navigate('/RawAuth');
      return;
    }
    loadData();
  }, [loadData, navigate, userType]);

  // ============================================
  // FILTERING & SORTING
  // ============================================
  useEffect(() => {
    let result = [...users];

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(user => 
        user.email?.toLowerCase().includes(term) ||
        user.phone?.toLowerCase().includes(term) ||
        user.name?.toLowerCase().includes(term) ||
        user.organisation?.toLowerCase().includes(term) ||
        user.company_name?.toLowerCase().includes(term)
      );
    }

    // Role filter
    if (filterRole !== 'all') {
      result = result.filter(user => user.user_type === filterRole);
    }

    // Status filter
    if (filterStatus === 'active') {
      result = result.filter(user => user.is_active);
    } else if (filterStatus === 'inactive') {
      result = result.filter(user => !user.is_active);
    } else if (filterStatus === 'verified') {
      result = result.filter(user => user.is_verified);
    } else if (filterStatus === 'unverified') {
      result = result.filter(user => !user.is_verified);
    }

    // Sorting
    switch (sortBy) {
      case 'newest':
        result.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        break;
      case 'oldest':
        result.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
        break;
      case 'email':
        result.sort((a, b) => a.email?.localeCompare(b.email || ''));
        break;
      case 'role':
        result.sort((a, b) => a.user_type?.localeCompare(b.user_type || ''));
        break;
      default:
        break;
    }

    setFilteredUsers(result);
  }, [users, searchTerm, filterRole, filterStatus, sortBy]);

  // ============================================
  // HANDLERS
  // ============================================
 

  const handleLogout = useCallback(() => {
    localStorage.clear();
    navigate('/RawAuth');
    setSnackbar({ open: true, message: 'Logged out successfully', severity: 'info' });
  }, [navigate]);

  const handleViewUser = (user) => {
    setSelectedUser(user);
    setUserDetailsDialog(true);
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setFormData({
      email: user.email || '',
      password: '',
      phone: user.phone || '',
      user_type: user.user_type || 'buyer',
      is_active: user.is_active || false,
      is_verified: user.is_verified || false,
      name: user.name || '',
      organisation: user.organisation || '',
      industry: user.industry || '',
      company_name: user.company_name || '',
      rc_number: user.rc_number || '',
      office_address: user.office_address || '',
    });
    setEditUserDialog(true);
  };

  const handleDeleteUser = (user) => {
    setSelectedUser(user);
    setDeleteUserDialog(true);
  };

  const handleFormChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedUsers(filteredUsers.map(u => u.id));
    } else {
      setSelectedUsers([]);
    }
  };

  const handleSelectUser = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId) 
        : [...prev, userId]
    );
  };

  const executeBulkAction = async () => {
    try {
      const userDataStr = localStorage.getItem('user_data');
      if (!userDataStr) return;

      const userData = JSON.parse(userDataStr);
      const adminId = userData.id;

      const response = await fetch(`${API_ENDPOINTS.USERS.TOGGLE_STATUS}?user_id=${adminId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: bulkAction,
          user_ids: selectedUsers,
        }),
      });

      const result = await response.json();
      
      if (result.success) {
        setSnackbar({ open: true, message: `${selectedUsers.length} users updated successfully`, severity: 'success' });
        setBulkActionDialog(false);
        setSelectedUsers([]);
        fetchUsers();
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
      logger.error('Error executing bulk action:', error);
      setSnackbar({ open: true, message: error.message || 'Failed to execute bulk action', severity: 'error' });
    }
  };

  // ============================================
  // RENDER FUNCTIONS
  // ============================================
  const renderStats = () => (
    <Grid container spacing={2.5} sx={{ mb: 3.5 }}>
      <Grid size={{ xs: 6, sm: 4, md: 2 }}>
        <Card sx={{ borderRadius: 3, p: 2.5, textAlign: 'center', bgcolor: alpha(COLORS.primary, 0.04), border: `1px solid ${alpha(COLORS.primary, 0.08)}` }}>
          <Typography variant="h4" fontWeight={800} color={COLORS.primary}>{stats.total}</Typography>
          <Typography variant="caption" color="textSecondary" fontWeight={600}>Total Users</Typography>
        </Card>
      </Grid>
      <Grid size={{ xs: 6, sm: 4, md: 2 }}>
        <Card sx={{ borderRadius: 3, p: 2.5, textAlign: 'center', bgcolor: alpha(COLORS.success, 0.04), border: `1px solid ${alpha(COLORS.success, 0.08)}` }}>
          <Typography variant="h4" fontWeight={800} color={COLORS.success}>{stats.active}</Typography>
          <Typography variant="caption" color="textSecondary" fontWeight={600}>Active</Typography>
        </Card>
      </Grid>
      <Grid size={{ xs: 6, sm: 4, md: 2 }}>
        <Card sx={{ borderRadius: 3, p: 2.5, textAlign: 'center', bgcolor: alpha(COLORS.error, 0.04), border: `1px solid ${alpha(COLORS.error, 0.08)}` }}>
          <Typography variant="h4" fontWeight={800} color={COLORS.error}>{stats.inactive}</Typography>
          <Typography variant="caption" color="textSecondary" fontWeight={600}>Inactive</Typography>
        </Card>
      </Grid>
      <Grid size={{ xs: 6, sm: 4, md: 2 }}>
        <Card sx={{ borderRadius: 3, p: 2.5, textAlign: 'center', bgcolor: alpha(COLORS.success, 0.04), border: `1px solid ${alpha(COLORS.success, 0.08)}` }}>
          <Typography variant="h4" fontWeight={800} color={COLORS.success}>{stats.verified}</Typography>
          <Typography variant="caption" color="textSecondary" fontWeight={600}>Verified</Typography>
        </Card>
      </Grid>
      <Grid size={{ xs: 6, sm: 4, md: 2 }}>
        <Card sx={{ borderRadius: 3, p: 2.5, textAlign: 'center', bgcolor: alpha(COLORS.warning, 0.04), border: `1px solid ${alpha(COLORS.warning, 0.08)}` }}>
          <Typography variant="h4" fontWeight={800} color={COLORS.warning}>{stats.unverified}</Typography>
          <Typography variant="caption" color="textSecondary" fontWeight={600}>Unverified</Typography>
        </Card>
      </Grid>
      <Grid size={{ xs: 6, sm: 4, md: 2 }}>
        <Card sx={{ borderRadius: 3, p: 2.5, textAlign: 'center', bgcolor: alpha(COLORS.gold, 0.04), border: `1px solid ${alpha(COLORS.gold, 0.08)}` }}>
          <Typography variant="h4" fontWeight={800} color={COLORS.gold}>{stats.admins}</Typography>
          <Typography variant="caption" color="textSecondary" fontWeight={600}>Admins</Typography>
        </Card>
      </Grid>
    </Grid>
  );

  const renderFilters = () => (
    <Paper sx={{ p: 2.5, mb: 3.5, borderRadius: 4, border: `1px solid ${COLORS.grey[200]}`, background: '#FFFFFF' }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
        <TextField
          placeholder="Search users by email, name..."
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: COLORS.grey[500] }} />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchTerm('')}>
                  <ClearIcon fontSize="small" sx={{ color: COLORS.grey[500] }} />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 200, flex: 1 }}
        />
        
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <Select
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
            displayEmpty
            sx={{ borderRadius: 2, fontWeight: 600 }}
          >
            <MenuItem value="all" sx={{ fontWeight: 600 }}>All Roles</MenuItem>
            <MenuItem value="buyer" sx={{ fontWeight: 500 }}>Buyers</MenuItem>
            <MenuItem value="supplier" sx={{ fontWeight: 500 }}>Suppliers</MenuItem>
            <MenuItem value="super_admin" sx={{ fontWeight: 500 }}>Admins</MenuItem>
          </Select>
        </FormControl>
        
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <Select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            displayEmpty
            sx={{ borderRadius: 2, fontWeight: 600 }}
          >
            <MenuItem value="all" sx={{ fontWeight: 600 }}>All Status</MenuItem>
            <MenuItem value="active" sx={{ fontWeight: 500 }}>Active</MenuItem>
            <MenuItem value="inactive" sx={{ fontWeight: 500 }}>Inactive</MenuItem>
            <MenuItem value="verified" sx={{ fontWeight: 500 }}>Verified</MenuItem>
            <MenuItem value="unverified" sx={{ fontWeight: 500 }}>Unverified</MenuItem>
          </Select>
        </FormControl>
        
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <Select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            displayEmpty
            sx={{ borderRadius: 2, fontWeight: 600 }}
          >
            <MenuItem value="newest" sx={{ fontWeight: 600 }}>Newest First</MenuItem>
            <MenuItem value="oldest" sx={{ fontWeight: 500 }}>Oldest First</MenuItem>
            <MenuItem value="email" sx={{ fontWeight: 500 }}>By Email</MenuItem>
            <MenuItem value="role" sx={{ fontWeight: 500 }}>By Role</MenuItem>
          </Select>
        </FormControl>
        
        <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
          <Tooltip title="Refresh">
            <IconButton onClick={loadData} size="small" sx={{ bgcolor: COLORS.grey[100], borderRadius: 2 }}>
              <RefreshIcon sx={{ color: COLORS.grey[700] }} />
            </IconButton>
          </Tooltip>
      
        </Box>
      </Box>
    </Paper>
  );

  const renderTable = () => (
    <TableContainer component={Paper} sx={{ borderRadius: 4, border: `1px solid ${COLORS.grey[200]}` }}>
      <Table>
        <TableHead sx={{ bgcolor: COLORS.grey[50] }}>
          <TableRow>
            <TableCell padding="checkbox">
              <Checkbox
                checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
                indeterminate={selectedUsers.length > 0 && selectedUsers.length < filteredUsers.length}
                onChange={handleSelectAll}
              />
            </TableCell>
            <TableCell sx={{ fontWeight: 800, color: COLORS.grey[700] }}>User</TableCell>
            <TableCell sx={{ fontWeight: 800, color: COLORS.grey[700] }}>Email</TableCell>
            <TableCell sx={{ fontWeight: 800, color: COLORS.grey[700] }}>Role</TableCell>
            <TableCell sx={{ fontWeight: 800, color: COLORS.grey[700] }}>Status</TableCell>
            <TableCell sx={{ fontWeight: 800, color: COLORS.grey[700] }}>Joined</TableCell>
            <TableCell sx={{ fontWeight: 800, color: COLORS.grey[700] }} align="center">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredUsers.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                <Typography color="textSecondary" fontWeight={500}>No users found</Typography>
              </TableCell>
            </TableRow>
          ) : (
            filteredUsers
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((user) => (
                <TableRow key={user.id} hover>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedUsers.includes(user.id)}
                      onChange={() => handleSelectUser(user.id)}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar sx={{ width: 32, height: 32, bgcolor: alpha(COLORS.primary, 0.08), color: COLORS.primary, fontSize: '0.8rem', fontWeight: 700 }}>
                        {user.email?.charAt(0).toUpperCase() || 'U'}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {user.name || user.company_name || 'N/A'}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {user.organisation || user.company_name || ''}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{user.email}</Typography>
                  </TableCell>
                  <TableCell>
                    <UserTypeChip userType={user.user_type} />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <UserStatusChip status={user.is_active ? 'active' : 'inactive'} />
                      {user.is_verified && (
                        <Tooltip title="Verified">
                          <VerifiedIcon sx={{ fontSize: 16, color: COLORS.success }} />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" fontWeight={500}>
                      {formatDate(user.created_at)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={0.5} justifyContent="center">
                      <Tooltip title="View Details">
                        <IconButton size="small" onClick={() => handleViewUser(user)} sx={{ color: COLORS.primary }}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => handleEditUser(user)} sx={{ color: COLORS.info }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={user.is_active ? 'Deactivate' : 'Activate'}>
                        <IconButton 
                          size="small" 
                          onClick={() => toggleUserStatus(user.id, 'toggle_active')}
                          sx={{ color: user.is_active ? COLORS.warning : COLORS.success }}
                        >
                          {user.is_active ? <BlockIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => handleDeleteUser(user)} sx={{ color: COLORS.error }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
          )}
        </TableBody>
      </Table>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={filteredUsers.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
      />
    </TableContainer>
  );

  // ============================================
  // MAIN RENDER
  // ============================================
  if (loading && users.length === 0) {
    return <Preloader text="Loading Users..." size={60} fullScreen />;
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: COLORS.grey[50] }}>

      
     <Sidebar
  open={drawerOpen}
  onClose={() => setDrawerOpen(false)}
  isMobile={isMobile}
  selectedTab={selectedTab}
  onTabChange={setSelectedTab}  // ← Just update state
  userData={userData}
  onLogout={handleLogout}
/>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          width: { xs: '100%', md: 'calc(100% - 280px)' },
        }}
      >
        <Header
          isMobile={isMobile}
          onDrawerToggle={() => setDrawerOpen(true)}
          selectedTab={selectedTab}
          menuItems={[]}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          cartItemCount={0}
          onCartOpen={() => {}}
          notifications={[]}
          userData={userData}
          onLogout={handleLogout}
          onChangePassword={() => {}}
        
        />

        <Box sx={{ flex: 1, p: { xs: 2, sm: 3 }, overflow: 'auto' }}>
          {/* Header */}
          <Box sx={{ mb: 3.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography variant="h4" fontWeight={800} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <PeopleIcon sx={{ color: COLORS.primary, fontSize: 32 }} />
                  User Management
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {filteredUsers.length} users found • Last updated: {lastRefreshed.toLocaleString()}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                {selectedUsers.length > 0 && (
                  <Button
                    variant="outlined"
                    onClick={() => setBulkActionDialog(true)}
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                  >
                    Bulk Action ({selectedUsers.length})
                  </Button>
                )}
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={loadData}
                  sx={{ borderRadius: 2, textTransform: 'none' }}
                >
                  Refresh
                </Button>
           
              </Box>
            </Box>
          </Box>

          {/* Stats */}
          {renderStats()}

          {/* Filters */}
          {renderFilters()}

          {/* Table */}
          {renderTable()}
        </Box>

        <Footer />
      </Box>

      {/* Dialogs */}
      {/* User Details Dialog */}
      <Dialog
        open={userDetailsDialog}
        onClose={() => setUserDetailsDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 4, maxHeight: '90vh' } }}
      >
        {selectedUser && (
          <>
            <DialogTitle sx={{ p: 3, pb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ width: 48, height: 48, bgcolor: alpha(COLORS.primary, 0.08), color: COLORS.primary, fontSize: '1.2rem', fontWeight: 700 }}>
                    {selectedUser.email?.charAt(0).toUpperCase() || 'U'}
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight={700}>
                      {selectedUser.name || selectedUser.company_name || 'User'}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {selectedUser.email}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <UserTypeChip userType={selectedUser.user_type} />
                  <UserStatusChip status={selectedUser.is_active ? 'active' : 'inactive'} />
                  <IconButton onClick={() => setUserDetailsDialog(false)}>
                    <CloseIcon />
                  </IconButton>
                </Box>
              </Box>
            </DialogTitle>
            <DialogContent dividers sx={{ p: 3 }}>
              <Grid container spacing={3}>
                {/* Basic Info */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <Paper sx={{ p: 2.5, bgcolor: COLORS.grey[50], borderRadius: 3 }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ color: COLORS.grey[600], mb: 2, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                      <PersonIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                      Basic Information
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <EmailIcon sx={{ fontSize: 16, color: COLORS.grey[400] }} />
                        <Typography variant="body2">{selectedUser.email}</Typography>
                      </Box>
                      {selectedUser.phone && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PhoneIcon sx={{ fontSize: 16, color: COLORS.grey[400] }} />
                          <Typography variant="body2">{selectedUser.phone}</Typography>
                        </Box>
                      )}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CalendarTodayIcon sx={{ fontSize: 16, color: COLORS.grey[400] }} />
                        <Typography variant="body2">Joined: {formatDate(selectedUser.created_at)}</Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>

                {/* Role & Status */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <Paper sx={{ p: 2.5, bgcolor: COLORS.grey[50], borderRadius: 3 }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ color: COLORS.grey[600], mb: 2, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                      <AdminPanelSettingsIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                      Role & Status
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" fontWeight={600}>Role:</Typography>
                        <UserTypeChip userType={selectedUser.user_type} />
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" fontWeight={600}>Status:</Typography>
                        <UserStatusChip status={selectedUser.is_active ? 'active' : 'inactive'} />
                        {selectedUser.is_verified && (
                          <Chip label="Verified" size="small" sx={{ bgcolor: alpha(COLORS.success, 0.1), color: COLORS.success, fontWeight: 600 }} />
                        )}
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ p: 2.5 }}>
              <Button onClick={() => setUserDetailsDialog(false)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Create User Dialog */}
      <Dialog
        open={createUserDialog}
        onClose={() => setCreateUserDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 4, maxHeight: '90vh' } }}
      >
        <DialogTitle sx={{ p: 3, pb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" fontWeight={700}>
              <PersonAddIcon sx={{ verticalAlign: 'middle', mr: 1, color: COLORS.primary }} />
              Create New User
            </Typography>
            <IconButton onClick={() => setCreateUserDialog(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          <Grid container spacing={2.5}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="Email Address"
                name="email"
                value={formData.email}
                onChange={handleFormChange}
                fullWidth
                required
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="Password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleFormChange}
                fullWidth
                required
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="Phone Number"
                name="phone"
                value={formData.phone}
                onChange={handleFormChange}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth size="small">
                <Select
                  label="User Type"
                  name="user_type"
                  value={formData.user_type}
                  onChange={handleFormChange}
                >
                  <MenuItem value="buyer">Buyer</MenuItem>
                  <MenuItem value="supplier">Supplier</MenuItem>
                  <MenuItem value="super_admin">Super Admin</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <FormControlLabel
                control={
                  <Switch
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleFormChange}
                    color="success"
                  />
                }
                label="Active"
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <FormControlLabel
                control={
                  <Switch
                    name="is_verified"
                    checked={formData.is_verified}
                    onChange={handleFormChange}
                    color="success"
                  />
                }
                label="Verified"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setCreateUserDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={createUser}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 700,
              background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryLight})`,
            }}
          >
            Create User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete User Dialog */}
      <Dialog
        open={deleteUserDialog}
        onClose={() => setDeleteUserDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 4 } }}
      >
        <DialogTitle sx={{ p: 3, pb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CancelIcon sx={{ color: COLORS.error }} />
            <Typography variant="h6" fontWeight={700}>Delete User</Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3, pt: 1 }}>
          <Typography variant="body2" sx={{ color: COLORS.grey[600] }}>
            Are you sure you want to delete the user <strong>{selectedUser?.email}</strong>?
          </Typography>
          <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setDeleteUserDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => deleteUser(selectedUser?.id)}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
          >
            Delete User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Action Dialog */}
      <Dialog
        open={bulkActionDialog}
        onClose={() => setBulkActionDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 4 } }}
      >
        <DialogTitle sx={{ p: 3, pb: 2 }}>
          <Typography variant="h6" fontWeight={700}>Bulk Action</Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 3, pt: 1 }}>
          <Typography variant="body2" sx={{ color: COLORS.grey[600], mb: 2 }}>
            Select action for {selectedUsers.length} users:
          </Typography>
          <FormControl fullWidth size="small">
            <Select
              value={bulkAction}
              onChange={(e) => setBulkAction(e.target.value)}
              displayEmpty
            >
              <MenuItem value="" disabled>Select action</MenuItem>
              <MenuItem value="activate">Activate All</MenuItem>
              <MenuItem value="deactivate">Deactivate All</MenuItem>
              <MenuItem value="verify">Verify All</MenuItem>
              <MenuItem value="unverify">Unverify All</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setBulkActionDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={executeBulkAction}
            disabled={!bulkAction}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 700,
              background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryLight})`,
            }}
          >
            Apply to {selectedUsers.length} Users
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        TransitionComponent={Slide}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ borderRadius: 3, fontWeight: 600 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserManagement;